import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { getTenantById } from '@tenants';
import { useUserContext } from 'contexts';
import { evaluateConsent } from 'domain/consent';
import { Namespaces } from 'types/locales';
import { SiteLanguage } from 'types/locales';
import { useTenantId } from './use-tenant-id';

const DEFAULT_PRIVACY_POLICY_WITH_LINKS =
  'By clicking "{{buttonText}}", you agree to our <privacyPolicyLink>Privacy Policy</privacyPolicyLink> and <termsOfUseLink>Terms of Use</termsOfUseLink>.';
const DEFAULT_PRIVACY_POLICY_TEXT =
  'By clicking "{{buttonText}}", you agree to our Privacy Policy and Terms of Use.';

/**
 * Resolves consent labels for the current tenant and user location.
 *
 * The profile is determined by `evaluateConsent(tenant.consent, countryCode)` —
 * no manual tenant-ID or isInCanada branching.
 *
 * Returns:
 * - `privacyPolicyConsentLabel`      — JSX element for the privacy checkbox
 * - `marketingCommunicationsConsentLabel` — plain string for the marketing checkbox
 * - `fubConsentDisclaimerText`        — plain-text version for FUB API payload
 */
const useConsentLabels = (buttonText: string, countryCodeOverride?: string | null) => {
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const { userLocation } = useUserContext();
  const { t, i18n } = useTranslation(Namespaces.MODAL);

  const countryCode = countryCodeOverride ?? userLocation?.countryCode ?? null;
  const { labels } = evaluateConsent(tenant.consent, countryCode);

  const privacyPolicyUrl = tenant.theme.privacyPolicy(i18n.language as SiteLanguage);
  const termsOfUseUrl = tenant.theme.termsOfUse(i18n.language as SiteLanguage);

  const privacyPolicyConsentLabel = useMemo(() => (
    <p>
      <Trans
        i18nKey={labels.privacy}
        values={{ buttonText }}
        defaults={DEFAULT_PRIVACY_POLICY_WITH_LINKS}
        components={{
          privacyPolicyLink: <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" />,
          termsOfUseLink:    <a href={termsOfUseUrl}    target="_blank" rel="noopener noreferrer" />,
        }}
      />
    </p>
  ), [labels.privacy, buttonText, privacyPolicyUrl, termsOfUseUrl]);

  const marketingCommunicationsConsentLabel = useMemo(
    () => t(labels.marketing, { buttonText, defaultValue: '' }),
    [t, labels.marketing, buttonText]
  );

  const fubConsentDisclaimerText = useMemo(() => {
    const privacyPlainText = t(labels.privacy, {
      buttonText,
      defaultValue: DEFAULT_PRIVACY_POLICY_TEXT,
    })
      .replace(/<privacyPolicyLink>/g, '')
      .replace(/<\/privacyPolicyLink>/g, ` (${privacyPolicyUrl})`)
      .replace(/<termsOfUseLink>/g, '')
      .replace(/<\/termsOfUseLink>/g, ` (${termsOfUseUrl})`);

    return `\n"${privacyPlainText}"\n"${marketingCommunicationsConsentLabel}"`;
  }, [t, labels.privacy, buttonText, privacyPolicyUrl, termsOfUseUrl, marketingCommunicationsConsentLabel]);

  return { privacyPolicyConsentLabel, marketingCommunicationsConsentLabel, fubConsentDisclaimerText };
};

export { useConsentLabels };
